import { WorkflowStepFailures } from '@/components/workflow/WorkflowStepFailures';
import { acoesFalhas, resumoFalhas, rotuloEtapa } from '@/lib/workflow/stepActionFailures';
/**
 * WorkflowProcessPanel - Processo do registro em um bloco único e compacto
 *
 * Funde, em uma só superfície densa, o que antes eram dois cartões grandes na
 * aba Resumo do negócio: a trilha da esteira (etapas concluídas, atual e
 * próximas) e as pendências humanas (aprovar / reprovar / concluir).
 *
 * Leiaute em duas faixas:
 *   1) cabeçalho de uma linha  → nome do processo, versão, status, "Somente
 *      minhas" e "Reavaliar";
 *   2) timeline vertical       → etapas detalhadas, ligadas por uma linha
 *      cronológica, com status, datas, prazos, gatilhos e pendências;
 *      Cada etapa concentra suas pendências, ações e decisões históricas.
 *
 * Camada de apresentação: nenhuma mudança no motor, permissão ou regra.
 * Reaproveita a timeline enriquecida pela RPC existente.
 *
 * @version 1.9.0
 * RELEASE: 2026-09-13 v1.9.0 - Todas as fases ganham expansão somente leitura
 *          com configuração, filtros, ações, execuções e caminhos sanitizados.
 * RELEASE: 2026-09-13 v1.8.0 - Decisões de cada etapa passam a formar uma
 *          timeline interna compacta, ordenada da mais antiga para a mais
 *          recente. Somente apresentação; dados e regras permanecem iguais.
 * RELEASE: 2026-09-13 v1.7.2 - Histórico de decisões exibe também o motivo
 *          original da aprovação (description da pendência), acima da
 *          justificativa digitada pelo usuário. Somente frontend.
 * RELEASE: 2026-09-13 v1.7.1 - Justificativa do usuário passa a usar barra
 *          vertical grossa à esquerda (estilo citação), conforme referência visual.
 * RELEASE: 2026-09-13 v1.7.0 - Alinha botões de aprovação/reprovação à direita,
 *          destaca justificativa do usuário no histórico e pinta a etapa 'fim'
 *          concluída em vermelho pastel. Ajustes puramente visuais.
 * RELEASE: 2026-09-13 v1.6.0 - Remove fundos semânticos dos cartões e badges
 *          de status; pendências e decisões passam a aparecer dentro da etapa
 *          correspondente da timeline. Nenhuma regra do Workflow foi alterada.
 * RELEASE: 2026-09-13 v1.5.0 - Trilha horizontal substituída por timeline
 *          vertical detalhada, em tons semânticos pastéis. Preserva menu
 *          lateral, pendências, decisões, permissões e ações existentes.
 * RELEASE: 2026-09-03 v1.4.1 - Histórico de decisões repaginado como timeline
 *          vertical: linha conectora, círculos de status, cartões espaçados e
 *          melhor hierarquia visual. Sem mudança de dados ou comportamento.
 * RELEASE: 2026-09-03 v1.4.0 - Modo `groupByStatus` passa a ser mestre-detalhe:
 *          lista de instâncias agrupadas (ativos / concluídos / cancelados) à
 *          esquerda e o detalhe do fluxo selecionado à direita. Modo padrão
 *          (lista simples) inalterado.
 * RELEASE: 2026-09-03 v1.3.0 - Prop opcional `groupByStatus`: separa os processos
 *          em "ativos", "concluídos" e "cancelados / com erro" (aba Fluxos de
 *          trabalho do modal de negócio). Sem a prop, nada muda em tela.
 * RELEASE: 2026-09-03 v1.2.0 - Um bloco por processo: o registro pode estar em
 *          mais de um processo ao mesmo tempo (fluxo padrão + fluxo por regra de
 *          valor). Consome a chave 'processos' da RPC v1.6.0 e separa as
 *          pendências por instância. Com um único processo, nada muda em tela.
 * RELEASE: 2026-09-03 v1.1.0 - Processo em status final (cancelado/concluído/erro)
 *          não exibe mais etapa "atual" nem próximos passos; a etapa que ficou
 *          aberta aparece como cancelada com o processo. Rótulos de status
 *          passam a cobrir os valores gravados pelo motor (pt-BR).
 * RELEASE: 2026-09-02 v1.0.0 - Criação: substitui WorkflowProgressTrail +
 *          WorkflowPendingTasksPanel na aba Resumo por um bloco compacto.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  ArrowRight,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  GitBranch,
  Hourglass,
  MessageSquare,
  RefreshCw,
  Undo2,
  UserCheck,
  XCircle,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { logger } from '@/lib/logger';
import {
  useWorkflowInstanceTimeline,
  type WorkflowTrailActionDetail,
  type WorkflowTrailDetails,
  type WorkflowTrailNext,
} from '@/hooks/useWorkflowInstanceTimeline';
import { useWorkflowHumanTasks, type WorkflowHumanTask } from '@/hooks/useWorkflowHumanTasks';
import { usePermissions } from '@/hooks/usePermissions';
// InlineNotice: padrão unificado de mensagens inline (orientação da etapa)
import { InlineNotice } from '@/components/ui/inline-notice';
import { describeNodeConfig } from '@/components/workflow/workflowNodeMeta';
import { WorkflowConditionSummary } from '@/components/workflow/inspector/WorkflowConditionSummary';
import type { WorkflowNodeType } from '@/hooks/useWorkflowDesigner';


interface WorkflowProcessPanelProps {
  /** Tipo da entidade-raiz (ex.: 'crm_deal') */
  rootEntityType: string;
  /** Id da entidade-raiz */
  rootEntityId: string;
  /** Consulta habilitada (módulo Workflow ligado) */
  enabled?: boolean;
  /**
   * v1.3.0: separa os processos em seções (ativos / concluídos / cancelados).
   * Usado pela aba "Fluxos de trabalho" do modal de negócio. Quando ausente,
   * o comportamento continua idêntico ao anterior (lista única).
   */
  groupByStatus?: boolean;
}


/** Rótulo amigável para o status da instância (chaves em inglês e em português) */
const STATUS_LABEL: Record<string, string> = {
  running: 'Em andamento',
  completed: 'Concluído',
  cancelled: 'Cancelado',
  failed: 'Com erro',
  waiting: 'Aguardando',
  // Status gravados pelo motor (workflow_instance_state)
  ativo: 'Em andamento',
  aguardando: 'Aguardando',
  concluido: 'Concluído',
  cancelado: 'Cancelado',
  erro: 'Com erro',
  compensando: 'Revertendo',
  compensado: 'Revertido',
};

/** Cor do ponto de status (tokens semânticos) */
const STATUS_DOT: Record<string, string> = {
  running: 'bg-primary',
  completed: 'bg-success',
  cancelled: 'bg-muted-foreground',
  failed: 'bg-destructive',
  waiting: 'bg-warning',
  ativo: 'bg-primary',
  aguardando: 'bg-warning',
  concluido: 'bg-success',
  cancelado: 'bg-muted-foreground',
  erro: 'bg-destructive',
  compensando: 'bg-warning',
  compensado: 'bg-muted-foreground',
};

/** Status finais: processo encerrado, sem etapa atual nem próximos passos */
const STATUS_ENCERRADOS = new Set([
  'cancelado',
  'concluido',
  'erro',
  'compensado',
  'cancelled',
  'completed',
  'failed',
]);

/** Status finais de cancelamento (etapa aberta vira "cancelada com o processo") */
const STATUS_CANCELADOS = new Set(['cancelado', 'cancelled', 'compensado']);

/**
 * Rótulo, cor do badge, ícone e cor do círculo de cada resultado de pendência
 * humana já resolvida. Usado no histórico de decisões (aprovações / reprovações).
 */
const DECISAO_INFO: Record<
  string,
  {
    label: string;
    badge: string;
    icon: typeof CheckCircle2;
    iconColor: string;
    circleBg: string;
    circleRing: string;
  }
> = {
  aprovado: {
    label: 'Aprovado',
    badge: 'bg-success/15 text-success',
    icon: CheckCircle2,
    iconColor: 'text-success',
    circleBg: 'bg-success/10',
    circleRing: 'ring-success/20',
  },
  reprovado: {
    label: 'Reprovado',
    badge: 'bg-destructive/15 text-destructive',
    icon: XCircle,
    iconColor: 'text-destructive',
    circleBg: 'bg-destructive/10',
    circleRing: 'ring-destructive/20',
  },
  concluido: {
    label: 'Concluído',
    badge: 'bg-muted text-muted-foreground',
    icon: Check,
    iconColor: 'text-muted-foreground',
    circleBg: 'bg-muted',
    circleRing: 'ring-muted',
  },
  cancelado: {
    label: 'Cancelado',
    badge: 'bg-muted text-muted-foreground',
    icon: XCircle,
    iconColor: 'text-muted-foreground',
    circleBg: 'bg-muted',
    circleRing: 'ring-muted',
  },
  expirado: {
    label: 'Expirado',
    badge: 'bg-warning/15 text-warning',
    icon: Clock,
    iconColor: 'text-warning',
    circleBg: 'bg-warning/10',
    circleRing: 'ring-warning/20',
  },
};

/** Formata data/hora no padrão brasileiro */
function formatarData(valor?: string | null): string {
  if (!valor) return '—';
  return new Date(valor).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Tempo relativo curto (ex.: "há 2h", "há 3 d") para o cabeçalho */
function tempoRelativo(valor?: string | null): string | null {
  if (!valor) return null;
  const ms = Date.now() - new Date(valor).getTime();
  if (Number.isNaN(ms) || ms < 0) return null;
  const min = Math.floor(ms / 60_000);
  if (min < 1) return 'agora';
  if (min < 60) return `há ${min} min`;
  const horas = Math.floor(min / 60);
  if (horas < 24) return `há ${horas}h`;
  return `há ${Math.floor(horas / 24)} d`;
}

/** Descreve, em português, o que dispara o próximo passo */
function descreverGatilho(passo: WorkflowTrailNext): string {
  if (passo.event_code) return `Quando: ${passo.event_label || passo.event_code}`;
  if (passo.tem_condicao) return 'Quando a condição configurada for atendida';
  if (passo.is_default) return 'Caminho padrão (avanço automático)';
  return 'Avanço automático';
}

/** Ícone do próximo passo conforme o tipo do bloco de destino */
function IconePasso({ passo, className }: { passo: WorkflowTrailNext; className?: string }) {
  const cls = cn('h-3 w-3', className);
  if (passo.is_regression) return <Undo2 className={cls} />;
  if (passo.node_type === 'human_task') return <UserCheck className={cls} />;
  if (passo.node_type === 'wait' || passo.node_type === 'timer') return <Hourglass className={cls} />;
  if (passo.node_type?.includes('gateway') || passo.node_type === 'decision')
    return <GitBranch className={cls} />;
  return <ArrowRight className={cls} />;
}

/** Rótulos de configuração que não pertencem a contratos de ação. */
const ROTULOS_CONFIGURACAO: Record<string, string> = {
  gateway_kind: 'Modo',
  join_policy: 'Regra de junção',
  join_count: 'Quantidade de ramos',
  wait_mode: 'Modo de espera',
  duration_minutes: 'Duração',
  wake_at: 'Data de liberação',
  event_code: 'Evento esperado',
  event_codes: 'Eventos esperados',
  event_match: 'Regra dos eventos',
  enforcement: 'Comportamento',
  guard_actions: 'Ações protegidas',
  human_kind: 'Tipo de tarefa',
  blocking: 'Bloqueante',
  due_in_minutes: 'Prazo',
  assignee_id: 'Responsável',
  workflow_id: 'Processo chamado',
};

/** Remove o prefixo técnico das referências portáteis resolvidas pelo servidor. */
function valorLegivel(valor: unknown): string {
  if (typeof valor === 'boolean') return valor ? 'Sim' : 'Não';
  if (valor === null || valor === undefined || valor === '') return '—';
  if (Array.isArray(valor)) return valor.map(valorLegivel).join(', ');
  if (typeof valor === 'object') return Object.values(valor as Record<string, unknown>).map(valorLegivel).join(' · ');
  const texto = String(valor);
  if (texto.startsWith('ref://')) return decodeURIComponent(texto.split('/').slice(3).join('/')).replace(/::/g, ' › ') || 'Referência indisponível';
  return texto;
}

/** Confirma se uma estrutura de condições possui ao menos uma regra útil. */
function temConteudo(valor: unknown): boolean {
  if (!valor) return false;
  if (Array.isArray(valor)) return valor.length > 0;
  if (typeof valor !== 'object') return String(valor).trim().length > 0;
  return Object.keys(valor as Record<string, unknown>).length > 0;
}

/** Parâmetros de uma ação apresentados conforme o contrato do catálogo. */
function ParametrosAcao({ acao }: { acao: WorkflowTrailActionDetail }) {
  const parametros = acao.parametros ?? {};
  const contrato = acao.contrato_parametros ?? [];
  const campos = Object.entries(parametros).filter(([, valor]) => valor !== null && valor !== undefined && valor !== '');
  if (campos.length === 0) return null;

  return (
    <dl className="mt-1.5 grid gap-x-4 gap-y-1 text-[10px] sm:grid-cols-2">
      {campos.map(([chave, valor]) => (
        <div key={chave} className="flex min-w-0 gap-1.5">
          <dt className="shrink-0 text-muted-foreground">{contrato.find((campo) => campo.key === chave)?.label ?? ROTULOS_CONFIGURACAO[chave] ?? chave.replace(/_/g, ' ')}:</dt>
          <dd className="min-w-0 break-words font-medium text-foreground">{valorLegivel(valor)}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Lista de ações configuradas ou executadas dentro da fase. */
function ListaAcoes({ titulo, acoes, executadas = false }: { titulo: string; acoes?: WorkflowTrailActionDetail[]; executadas?: boolean }) {
  if (!acoes?.length) return null;
  return (
    <section className="space-y-2">
      <h5 className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">{titulo}</h5>
      {acoes.map((acao, indice) => (
        <div key={`${acao.codigo}-${indice}`} className="border-l-2 border-border pl-3">
          <div className="flex flex-wrap items-center gap-2 text-[11px]">
            <span className="font-medium text-foreground">{acao.nome}</span>
            {executadas && acao.status && (
              <Badge variant="outline" className="h-5 px-1.5 text-[9px]">{acao.status}</Badge>
            )}
            {executadas && (acao.finalizado_em || acao.iniciado_em) && (
              <span className="text-[9px] text-muted-foreground">{formatarData(acao.finalizado_em ?? acao.iniciado_em)}</span>
            )}
          </div>
          <ParametrosAcao acao={acao} />
          {acao.erro && <p className="mt-1 text-[10px] text-destructive">Falha: {acao.erro}</p>}
        </div>
      ))}
    </section>
  );
}

/** Conteúdo compartilhado pela expansão de fases percorridas e próximas. */
export function DetalhesDaFase({ nodeType, detalhes }: { nodeType: string; detalhes?: WorkflowTrailDetails | null }) {
  if (!detalhes) return <p className="text-[10px] text-muted-foreground">Nenhum detalhe adicional foi configurado nesta fase.</p>;
  const config = detalhes.resumo_configuracao ?? {};
  const resumo = describeNodeConfig(nodeType as WorkflowNodeType, config);
  const configs = Object.entries(config).filter(([, valor]) => valor !== null && valor !== undefined && valor !== '');
  const caminhos = detalhes.caminhos_saida ?? [];

  return (
    <div className="space-y-4">
      {detalhes.origem_configuracao === 'historica' && (
        <InlineNotice variant="info" className="py-1 text-[10px]">Configuração preservada da versão em que esta fase foi executada.</InlineNotice>
      )}
      {(resumo || configs.length > 0) && (
        <section>
          <h5 className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">Configuração da fase</h5>
          {resumo && <p className="mt-1 text-[11px] font-medium text-foreground">{resumo}</p>}
          {configs.length > 0 && (
            <dl className="mt-1.5 grid gap-x-4 gap-y-1 text-[10px] sm:grid-cols-2">
              {configs.map(([chave, valor]) => (
                <div key={chave} className="flex min-w-0 gap-1.5">
                  <dt className="shrink-0 text-muted-foreground">{ROTULOS_CONFIGURACAO[chave] ?? chave.replace(/_/g, ' ')}:</dt>
                  <dd className="break-words font-medium text-foreground">{valorLegivel(valor)}</dd>
                </div>
              ))}
            </dl>
          )}
        </section>
      )}
      {temConteudo(detalhes.filtros_entrada) && (
        <section>
          <h5 className="mb-1.5 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">Filtros de entrada</h5>
          <WorkflowConditionSummary condicoes={detalhes.filtros_entrada} compacto />
        </section>
      )}
      {temConteudo(detalhes.condicoes_caminho) && (
        <section>
          <h5 className="mb-1.5 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">Condição para chegar aqui</h5>
          <WorkflowConditionSummary condicoes={detalhes.condicoes_caminho} compacto />
        </section>
      )}
      <ListaAcoes titulo="Ao entrar na fase" acoes={detalhes.acoes_entrada} />
      <ListaAcoes titulo="Ao concluir a fase" acoes={detalhes.acoes_conclusao} />
      <ListaAcoes titulo="Ações executadas" acoes={detalhes.acoes_executadas} executadas />
      {caminhos.length > 0 && (
        <section className="space-y-2">
          <h5 className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">Caminhos seguintes configurados</h5>
          {caminhos.map((caminho, indice) => (
            <div key={`${caminho.destino}-${indice}`} className="border-l-2 border-border pl-3 text-[10px]">
              <p className="font-medium text-foreground">{caminho.rotulo || caminho.evento_nome || 'Avanço automático'} <ArrowRight className="mx-1 inline h-3 w-3" /> {caminho.destino}</p>
              <div className="mt-1 flex flex-wrap gap-1.5 text-muted-foreground">
                {caminho.padrao && <span>Caminho padrão</span>}
                {caminho.regressao && <span>Retorno</span>}
                {caminho.enforcement === 'block' && <span>Bloqueia o avanço</span>}
                {caminho.enforcement === 'warn' && <span>Exibe aviso</span>}
              </div>
              {temConteudo(caminho.condicoes) && <WorkflowConditionSummary condicoes={caminho.condicoes} compacto className="mt-1.5" />}
            </div>
          ))}
        </section>
      )}
      {!resumo && configs.length === 0 && !temConteudo(detalhes.filtros_entrada) && !temConteudo(detalhes.condicoes_caminho) && !detalhes.acoes_entrada?.length && !detalhes.acoes_conclusao?.length && !detalhes.acoes_executadas?.length && caminhos.length === 0 && (
        <p className="text-[10px] text-muted-foreground">Nenhum detalhe adicional foi configurado nesta fase.</p>
      )}
    </div>
  );
}


/**
 * Container: um bloco por processo do registro.
 *
 * v1.2.0 — um mesmo negócio pode entrar em MAIS DE UM processo ao mesmo tempo
 * (ex.: fluxo padrão da venda + fluxo específico por regra de valor). A RPC
 * `get_workflow_instance_timeline` (v1.6.0) passou a devolver todos eles em
 * `processos`; aqui cada um ganha seu próprio bloco, com as pendências
 * separadas por instância. Com um único processo, o resultado é idêntico ao
 * leiaute anterior.
 */
export function WorkflowProcessPanel({
  rootEntityType,
  rootEntityId,
  enabled = true,
  groupByStatus = false,

}: WorkflowProcessPanelProps) {
  const { progresso, processos, isLoading, refetch } = useWorkflowInstanceTimeline({
    rootEntityType,
    rootEntityId,
    enabled,
  });

  // v1.4.1: o ouvinte de tempo real do processo foi elevado para o modal do
  // negócio (DealDetailsModalPremium) — aqui não é mais montado, evitando
  // canal duplicado, e continua cobrindo esta aba enquanto o modal está aberto.

  const [somenteMinhas, setSomenteMinhas] = useState(false);
  // v1.4.0: instância selecionada no modo mestre-detalhe (aba Fluxos de trabalho)
  const [instanciaSelecionada, setInstanciaSelecionada] = useState<string | null>(null);
  const [reavaliando, setReavaliando] = useState(false);

  const { toast } = useToast();
  // Resolver aprovação exige permissão dedicada (o banco revalida)
  const { canEdit } = usePermissions();
  const podeResolver = canEdit('workflow_approvals');

  const { tasks, isLoading: carregandoTarefas, refetch: refetchTarefas, resolve } = useWorkflowHumanTasks({
    rootEntityType,
    rootEntityId,
    onlyMine: somenteMinhas,
  });

  /**
   * Histórico de decisões (v1.3.0): mesma RPC, agora incluindo as pendências já
   * resolvidas do registro. Chave de cache própria — não afeta as demais telas.
   * Serve para exibir quantas vezes a etapa foi reprovada/aprovada e a
   * justificativa registrada em cada decisão.
   */
  const { tasks: tarefasComResolvidas } = useWorkflowHumanTasks({
    rootEntityType,
    rootEntityId,
    includeResolved: true,
  });

  const decisoes = useMemo(
    () =>
      (tarefasComResolvidas ?? [])
        .filter((t) => t.status !== 'pendente')
        // v1.8.0: sequência cronológica visual — evento mais recente embaixo.
        .sort((a, b) =>
          String(a.resolved_at ?? a.created_at).localeCompare(String(b.resolved_at ?? b.created_at)),
        ),
    [tarefasComResolvidas],
  );

  // Uma única avaliação por abertura do painel. A RPC usa a política gravada em
  // cada versão publicada e não faz busca, fila ou varredura de outros negócios.
  const aberturaAvaliadaRef = useRef<string | null>(null);
  useEffect(() => {
    if (!enabled || !rootEntityId || aberturaAvaliadaRef.current === rootEntityId) return;
    aberturaAvaliadaRef.current = rootEntityId;

    let ativo = true;
    const avaliarAoAbrir = async () => {
      const { error } = await supabase.rpc('workflow_reevaluate_entity', {
        p_entity_id: rootEntityId,
        p_entity_type: rootEntityType,
      });

      if (error) {
        // A abertura do negócio não deve ser bloqueada por falha de reavaliação.
        logger.warn('WorkflowProcessPanel', 'Falha na avaliação sob demanda ao abrir', {
          error: error.message,
          rootEntityId,
        });
        return;
      }

      if (ativo) await Promise.all([refetch(), refetchTarefas()]);
    };

    void avaliarAoAbrir();
    return () => {
      ativo = false;
    };
  }, [enabled, refetch, refetchTarefas, rootEntityId, rootEntityType]);

  /**
   * Reavalia o registro contra a VERSÃO VIGENTE de cada processo publicado.
   * Regulariza negócios presos a um desenho antigo do processo.
   */
  const reavaliar = async () => {
    setReavaliando(true);
    try {
      const { error } = await supabase.rpc('workflow_reevaluate_entity', {
        p_entity_id: rootEntityId,
        p_entity_type: rootEntityType,
      });
      if (error) throw error;
      await refetch();
      toast({
        title: 'Processo reavaliado',
        description: 'O registro foi reavaliado contra a versão vigente do processo.',
      });
    } catch (err) {
      logger.error('[WorkflowProcessPanel] Falha ao reavaliar processo', err);
      toast({
        title: 'Não foi possível reavaliar',
        description: err instanceof Error ? err.message : 'Tente novamente em instantes.',
        variant: 'destructive',
      });
    } finally {
      setReavaliando(false);
    }
  };

  if (!enabled) return null;

  // Carregando: uma única linha discreta (não empurra o conteúdo abaixo)
  if (isLoading) {
    return (
      <div className="flex h-9 items-center rounded-lg border bg-card px-3 text-[11px] text-muted-foreground">
        Carregando a esteira…
      </div>
    );
  }

  // Sem instância: uma linha explicando, sem cartão grande
  if (!progresso || processos.length === 0) {
    return (
      <div className="flex h-9 items-center gap-2 rounded-lg border bg-card px-3 text-[11px] text-muted-foreground">
        <GitBranch className="h-3.5 w-3.5 shrink-0" />
        {/* Texto neutro: o painel atende negócio, cobrança, tarefa, retenção e jurídico */}
        Este registro ainda não entrou em nenhum processo publicado.
      </div>
    );
  }

  const varios = processos.length > 1;

  /** Renderiza um bloco de processo (mesma renderização em ambos os modos) */
  const renderBloco = (proc: (typeof processos)[number]) => (
    <ProcessoBloco
      key={proc.instance_id ?? proc.definicao?.id ?? Math.random().toString(36)}
      progresso={proc}
      // Cada bloco mostra apenas as pendências da sua própria instância
      tasks={tasks.filter((t) => t.instance_id === proc.instance_id)}
      // Histórico de decisões desta mesma instância (aprovações/reprovações)
      decisoes={decisoes.filter((t) => t.instance_id === proc.instance_id)}
      carregandoTarefas={carregandoTarefas}
      resolve={resolve}
      podeResolver={podeResolver}
      somenteMinhas={somenteMinhas}
      setSomenteMinhas={setSomenteMinhas}
      reavaliar={reavaliar}
      reavaliando={reavaliando}
    />
  );

  // v1.4.0: modo mestre-detalhe (aba "Fluxos de trabalho" do modal de negócio).
  // Coluna esquerda lista as instâncias agrupadas por situação; a coluna direita
  // mostra somente o processo selecionado, reaproveitando integralmente o
  // ProcessoBloco (trilha, pendências e histórico de decisões).
  if (groupByStatus) {
    const ativos = processos.filter((p) => !STATUS_ENCERRADOS.has(p.status ?? ''));
    const concluidos = processos.filter((p) =>
      ['concluido', 'completed'].includes(p.status ?? ''),
    );
    const encerrados = processos.filter(
      (p) =>
        STATUS_ENCERRADOS.has(p.status ?? '') &&
        !['concluido', 'completed'].includes(p.status ?? ''),
    );

    // Ordem de exibição da lista: ativos primeiro, depois concluídos e encerrados
    const ordenados = [...ativos, ...concluidos, ...encerrados];
    const chaveDe = (p: (typeof processos)[number]) =>
      p.instance_id ?? p.definicao?.id ?? '';

    // Seleção padrão: primeiro ativo (ou o primeiro da lista, se não houver ativo)
    const selecionadoAtual =
      ordenados.find((p) => chaveDe(p) === instanciaSelecionada) ?? ordenados[0];

    return (
      <div className="grid gap-3 md:grid-cols-[minmax(220px,260px)_1fr]">
        {/* Coluna esquerda: lista de instâncias agrupadas por situação */}
        <aside className="min-w-0 space-y-3 md:max-h-[70vh] md:overflow-y-auto md:pr-1 scrollbar-thin">
          <GrupoLista
            titulo="Ativos"
            itens={ativos}
            vazio="Nenhum fluxo ativo"
            chaveDe={chaveDe}
            selecionado={selecionadoAtual ? chaveDe(selecionadoAtual) : null}
            onSelecionar={setInstanciaSelecionada}
          />
          <GrupoLista
            titulo="Concluídos"
            itens={concluidos}
            chaveDe={chaveDe}
            selecionado={selecionadoAtual ? chaveDe(selecionadoAtual) : null}
            onSelecionar={setInstanciaSelecionada}
          />
          <GrupoLista
            titulo="Cancelados / com erro"
            itens={encerrados}
            chaveDe={chaveDe}
            selecionado={selecionadoAtual ? chaveDe(selecionadoAtual) : null}
            onSelecionar={setInstanciaSelecionada}
          />
        </aside>

        {/* Coluna direita: detalhe do processo selecionado */}
        <div className="min-w-0">
          {selecionadoAtual ? (
            renderBloco(selecionadoAtual)
          ) : (
            <div className="flex h-9 items-center rounded-lg border bg-card px-3 text-[11px] text-muted-foreground">
              Selecione um fluxo de trabalho para ver os detalhes.
            </div>
          )}
        </div>
      </div>
    );
  }


  return (
    <div className="space-y-2">
      {varios && (
        <p className="px-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          {processos.length} processos neste negócio
        </p>
      )}

      {processos.map(renderBloco)}
    </div>
  );
}

/**
 * Grupo da coluna esquerda (modo mestre-detalhe): título com contador e a
 * lista compacta de instâncias daquele grupo. Grupos vazios só aparecem quando
 * há uma mensagem de vazio configurada (caso de "Ativos").
 */
function GrupoLista({
  titulo,
  itens,
  vazio,
  chaveDe,
  selecionado,
  onSelecionar,
}: {
  titulo: string;
  itens: Array<any>;
  vazio?: string;
  chaveDe: (p: any) => string;
  selecionado: string | null;
  onSelecionar: (chave: string) => void;
}) {
  if (itens.length === 0 && !vazio) return null;

  return (
    <section className="space-y-1">
      <div className="flex items-center gap-1.5 px-0.5">
        <h4 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          {titulo}
        </h4>
        <span className="rounded-full bg-muted px-1.5 text-[10px] leading-4 text-muted-foreground">
          {itens.length}
        </span>
      </div>

      {itens.length === 0 ? (
        <p className="px-0.5 text-[11px] text-muted-foreground">{vazio}</p>
      ) : (
        <ul className="space-y-1">
          {itens.map((p) => {
            const chave = chaveDe(p);
            const ativo = chave === selecionado;
            const statusKey = p.status ?? '';
            const desde =
              tempoRelativo(p.completed_at ?? p.cancelled_at ?? p.started_at) ?? null;

            return (
              <li key={chave || Math.random().toString(36)}>
                <button
                  type="button"
                  onClick={() => onSelecionar(chave)}
                  className={cn(
                    'flex w-full items-start gap-2 rounded-lg border px-2 py-1.5 text-left transition-colors',
                    ativo
                      ? 'border-primary/40 bg-primary/5'
                      : 'border-border bg-card hover:bg-muted/50',
                  )}
                >
                  <span
                    className={cn(
                      'mt-1 h-1.5 w-1.5 shrink-0 rounded-full',
                      STATUS_DOT[statusKey] ?? 'bg-muted-foreground',
                    )}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[11px] font-medium text-foreground">
                      {p.definicao?.nome ?? 'Processo'}
                    </span>
                    <span className="block truncate text-[10px] text-muted-foreground">
                      {p.definicao?.versao != null ? `v${p.definicao.versao}` : '—'}
                      {desde ? ` · ${desde}` : ''}
                    </span>
                  </span>
                  <ChevronRight className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}


/** Props de um bloco de processo (apresentação pura) */
interface ProcessoBlocoProps {
  progresso: NonNullable<ReturnType<typeof useWorkflowInstanceTimeline>['progresso']>;
  tasks: WorkflowHumanTask[];
  /** Decisões já registradas (aprovadas/reprovadas/concluídas) desta instância */
  decisoes: WorkflowHumanTask[];
  carregandoTarefas: boolean;
  resolve: ReturnType<typeof useWorkflowHumanTasks>['resolve'];
  podeResolver: boolean;
  somenteMinhas: boolean;
  setSomenteMinhas: (v: boolean) => void;
  reavaliar: () => void;
  reavaliando: boolean;
}

/**
 * Timeline visual do histórico de decisões de uma instância.
 *
 * Reaproveita o padrão da timeline de contratos (linha vertical + círculo +
 * conteúdo lateral) para dar hierarquia e espaçamento às aprovações,
 * reprovações e conclusões já registradas.
 */
/** Um processo: cabeçalho, trilha e pendências */
function ProcessoBloco({
  progresso,
  tasks,
  decisoes,
  carregandoTarefas,
  resolve,
  podeResolver,
  somenteMinhas,
  setSomenteMinhas,
  reavaliar,
  reavaliando,
}: ProcessoBlocoProps) {
  const [justificativas, setJustificativas] = useState<Record<string, string>>({});
  const [expandidas, setExpandidas] = useState<Record<string, boolean>>({});
  // Detalhes completos de cada pendência (abertos pela seta)
  const [detalhes, setDetalhes] = useState<Record<string, boolean>>({});
  // Expansão independente da configuração de cada fase da timeline.
  const [detalhesFases, setDetalhesFases] = useState<Record<string, boolean>>({});
  const { toast } = useToast();



  // Etapas percorridas na SEQUÊNCIA DO FLUXO (mesma ordem do construtor),
  // usando o horário apenas como desempate. Sem repetir o mesmo bloco seguido.
  const etapas = useMemo(() => {
    const lista = [...(progresso.etapas ?? [])].sort((a, b) => {
      const oa = a.ordem_fluxo ?? 9999;
      const ob = b.ordem_fluxo ?? 9999;
      if (oa !== ob) return oa - ob;
      return String(a.ocorrido_em ?? '').localeCompare(String(b.ocorrido_em ?? ''));
    });
    return lista.filter((etapa, i) => i === 0 || etapa.node_id !== lista[i - 1].node_id);
  }, [progresso]);



  const statusKey = progresso.status ?? '';
  const statusLabel = STATUS_LABEL[statusKey] ?? statusKey ?? '—';
  // v1.1.0 (painel): processo em status final não tem etapa atual nem próximos
  // passos. A RPC (v1.5.0) já devolve 'encerrada'; o Set é defesa em profundidade
  // para instâncias antigas em cache.
  const encerrado = progresso.encerrada === true || STATUS_ENCERRADOS.has(statusKey);
  const cancelado = STATUS_CANCELADOS.has(statusKey);
  const atualId = encerrado ? null : (progresso.etapa_atual?.node_id ?? null);
  const desdeRelativo = tempoRelativo(progresso.etapa_atual?.entrou_em);
  const proximos = encerrado ? [] : progresso.proximos_passos.filter((p) => !p.is_regression);
  const proximoResumo = proximos[0] ?? null;
  // BR-WF-OS-002: alerta do cabeçalho mostra quantas ações falharam e o motivo
  const resumoAlerta = resumoFalhas(
    etapas.flatMap((e) => acoesFalhas(e.detalhes?.acoes_executadas)),
    progresso.error_summary,
  );

  /** Resolve o vínculo principal por ID e usa o rótulo apenas para registros legados. */
  const idsDasEtapas = useMemo(() => new Set(etapas.map((etapa) => etapa.node_id)), [etapas]);
  const etapaIdPorRotulo = useMemo(() => {
    const mapa = new Map<string, string>();
    for (const etapa of etapas) mapa.set(etapa.label.trim().toLocaleLowerCase('pt-BR'), etapa.node_id);
    return mapa;
  }, [etapas]);
  const resolverEtapaDaTarefa = (tarefa: WorkflowHumanTask): string | null => {
    if (idsDasEtapas.has(tarefa.node_id)) return tarefa.node_id;
    const rotulo = tarefa.node_label?.trim().toLocaleLowerCase('pt-BR');
    return rotulo ? etapaIdPorRotulo.get(rotulo) ?? null : null;
  };

  /** Indexa pendências e decisões pela etapa, sem alterar os dados recebidos. */
  const tarefasPorEtapa = useMemo(() => {
    const mapa = new Map<string, WorkflowHumanTask[]>();
    for (const tarefa of tasks) {
      const etapaId = resolverEtapaDaTarefa(tarefa);
      if (!etapaId) continue;
      const lista = mapa.get(etapaId) ?? [];
      lista.push(tarefa);
      mapa.set(etapaId, lista);
    }
    return mapa;
  }, [etapaIdPorRotulo, idsDasEtapas, tasks]);

  const decisoesPorEtapa = useMemo(() => {
    const mapa = new Map<string, WorkflowHumanTask[]>();
    for (const decisao of decisoes) {
      const etapaId = resolverEtapaDaTarefa(decisao);
      if (!etapaId) continue;
      const lista = mapa.get(etapaId) ?? [];
      lista.push(decisao);
      mapa.set(etapaId, lista);
    }
    return mapa;
  }, [decisoes, etapaIdPorRotulo, idsDasEtapas]);

  /** Registros legados sem etapa correspondente continuam visíveis ao final da trilha. */
  const tarefasSemEtapa = useMemo(
    () => tasks.filter((tarefa) => !resolverEtapaDaTarefa(tarefa)),
    [etapaIdPorRotulo, idsDasEtapas, tasks],
  );
  const decisoesSemEtapa = useMemo(
    () => decisoes.filter((decisao) => !resolverEtapaDaTarefa(decisao)),
    [decisoes, etapaIdPorRotulo, idsDasEtapas],
  );

  /** Abre a justificativa (obrigatória ao reprovar) */
  const abrirJustificativa = (id: string) => setExpandidas((atual) => ({ ...atual, [id]: true }));

  /** Ações de uma pendência, reaproveitadas no leiaute desktop e mobile */
  const renderAcoes = (t: WorkflowHumanTask) =>
    t.kind === 'aprovacao' ? (
      <>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 px-2.5 text-[11px] bg-destructive/10 text-destructive hover:bg-destructive/20 hover:text-destructive"
          disabled={resolve.isPending || !podeResolver}
          onClick={() => {
            const nota = justificativas[t.id]?.trim();
            if (!nota) {
              abrirJustificativa(t.id);
              toast({
                title: 'Justificativa obrigatória',
                description: 'Descreva o motivo antes de reprovar a pendência.',
              });
              return;
            }
            resolve.mutate({ id: t.id, decision: 'reprovado', note: nota });
          }}
        >
          <XCircle className="mr-1 h-3.5 w-3.5" /> Reprovar
        </Button>
        <Button
          size="sm"
          className="h-7 bg-success px-3 text-[11px] text-success-foreground hover:bg-success/90"
          disabled={resolve.isPending || !podeResolver}
          onClick={() =>
            resolve.mutate({ id: t.id, decision: 'aprovado', note: justificativas[t.id]?.trim() || undefined })
          }
        >
          <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Aprovar
        </Button>
      </>
    ) : (
      <Button
        size="sm"
        className="h-7 bg-success px-3 text-[11px] text-success-foreground hover:bg-success/90"
        disabled={resolve.isPending || !podeResolver}
        onClick={() => resolve.mutate({ id: t.id, decision: 'concluido' })}
      >
        <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Concluir
      </Button>
    );

  /** Conteúdo operacional da pendência, agora exibido dentro da própria etapa. */
  const renderPendencia = (t: WorkflowHumanTask) => {
    const aberta = !!detalhes[t.id];
    return (
      <div key={t.id} className="border-t pt-3 first:border-t-0 first:pt-0">
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
          <div className="flex min-w-0 items-center gap-2">
            <button
              type="button"
              aria-expanded={aberta}
              aria-label={aberta ? 'Recolher detalhes da pendência' : 'Expandir detalhes da pendência'}
              className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              onClick={() => setDetalhes((atual) => ({ ...atual, [t.id]: !atual[t.id] }))}
            >
              {aberta ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            </button>
            <span className="relative shrink-0">
              <SmartAvatar src={t.assignee_avatar_url ?? undefined} name={t.assignee_name ?? undefined} size="xs" />
              {t.blocking && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="absolute -right-1 -top-1 flex h-3 w-3 items-center justify-center rounded-full bg-warning text-[8px] font-bold text-background ring-2 ring-card">!</span>
                  </TooltipTrigger>
                  <TooltipContent>Bloqueante: segura o avanço da esteira</TooltipContent>
                </Tooltip>
              )}
            </span>
            <div className="min-w-0">
              <div className="flex min-w-0 items-center gap-1.5">
                <span className="truncate text-[12px] font-medium text-foreground">{t.title}</span>
                {t.sla_violated && <span className="text-[9px] font-medium text-destructive">Prazo vencido</span>}
              </div>
              <p className="truncate text-[10px] text-muted-foreground">
                {t.assignee_name ? `${t.assignee_name} · ` : ''}
                {proximoResumo ? `Próximo: ${proximoResumo.label} · ${descreverGatilho(proximoResumo)}` : t.definition_name ?? ''}
                {t.due_at && !t.sla_violated ? ` · Prazo ${formatarData(t.due_at)}` : ''}
              </p>
            </div>
          </div>
          <div className="ml-auto flex shrink-0 items-center gap-1">{renderAcoes(t)}</div>
        </div>

        {t.description && !aberta && (
          <InlineNotice variant="info" className="mt-2 py-1 text-[11px]">
            <span className="line-clamp-2">{t.description}</span>
          </InlineNotice>
        )}
        {aberta && (
          <dl className="mt-2 grid grid-cols-1 gap-x-4 gap-y-1 border-l-2 border-border pl-3 text-[10px] sm:grid-cols-2">
            <div className="flex gap-1.5"><dt className="text-muted-foreground">Processo:</dt><dd className="truncate text-foreground">{t.definition_name ?? '—'}</dd></div>
            <div className="flex gap-1.5"><dt className="text-muted-foreground">Etapa:</dt><dd className="truncate text-foreground">{t.node_label ?? '—'}</dd></div>
            <div className="flex gap-1.5"><dt className="text-muted-foreground">Tipo:</dt><dd className="text-foreground">{t.kind === 'aprovacao' ? 'Aprovação' : 'Tarefa'}{t.blocking ? ' · bloqueante' : ' · não bloqueante'}</dd></div>
            <div className="flex gap-1.5"><dt className="text-muted-foreground">Responsável:</dt><dd className="truncate text-foreground">{t.assignee_name ?? 'Não atribuído'}</dd></div>
            <div className="flex gap-1.5"><dt className="text-muted-foreground">Criada em:</dt><dd className="text-foreground">{formatarData(t.created_at)}</dd></div>
            <div className="flex gap-1.5"><dt className="text-muted-foreground">Prazo:</dt><dd className="text-foreground">{t.due_at ? formatarData(t.due_at) : 'Sem prazo'}</dd></div>
            {t.description && <div className="flex gap-1.5 sm:col-span-2"><dt className="shrink-0 text-muted-foreground">Descrição:</dt><dd className="text-foreground">{t.description}</dd></div>}
          </dl>
        )}
        {t.kind === 'aprovacao' && (expandidas[t.id] ? (
          <Textarea
            autoFocus
            rows={2}
            className="mt-2 min-h-[52px] resize-none text-[11px]"
            placeholder="Justificativa (obrigatória para reprovar)"
            value={justificativas[t.id] ?? ''}
            maxLength={500}
            onChange={(e) => setJustificativas((atual) => ({ ...atual, [t.id]: e.target.value.slice(0, 500) }))}
          />
        ) : (
          <button type="button" className="mt-2 flex items-center gap-1.5 text-[10px] text-muted-foreground/80 transition-colors hover:text-muted-foreground" onClick={() => abrirJustificativa(t.id)}>
            <MessageSquare className="h-3 w-3" />
            {justificativas[t.id]?.trim() ? justificativas[t.id] : 'Adicionar justificativa ou observação interna…'}
          </button>
        ))}
      </div>
    );
  };

  /** Resultado histórico exibido como ponto da timeline interna da etapa. */
  const renderDecisao = (d: WorkflowHumanTask, indice: number, total: number) => {
    const info = DECISAO_INFO[d.status] ?? DECISAO_INFO.concluido;
    const Icon = info.icon;
    return (
      <div key={`dec-${d.id}`} className="relative flex gap-3 pb-4 last:pb-0">
        {/* v1.8.0: conector fino transforma as interações em uma timeline inequívoca. */}
        {indice < total - 1 && (
          <span aria-hidden="true" className="absolute bottom-0 left-[9px] top-5 w-px bg-border" />
        )}
        <span className={cn('relative z-10 mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ring-2 ring-background', info.circleBg)}>
          <Icon className={cn('h-3 w-3', info.iconColor)} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 text-[10px]">
            <span className="font-medium text-foreground">{info.label}</span>
            <span className="text-muted-foreground">por {d.resolved_by_name ?? 'Sistema'}{d.resolved_at ? ` · ${formatarData(d.resolved_at)}` : ''}</span>
          </div>
          {/* v1.7.2: motivo original da aprovação, preservado no histórico */}
          {d.description && (
            <p className="mt-1 text-[10px] text-muted-foreground">
              <span className="font-medium">Motivo: </span>
              {d.description}
            </p>
          )}
          {d.resolution_note && (
            // v1.7.1: destaque com barra vertical grossa à esquerda (padrão citação)
            <p className="mt-1.5 rounded-r-md border-l-4 border-l-foreground/70 bg-muted/50 px-3 py-2 text-[11px] text-foreground">
              {d.resolution_note}
            </p>
          )}
        </div>
      </div>
    );
  };

  return (
    <TooltipProvider delayDuration={200}>
      <section
        id="workflow-progress-trail"
        className="overflow-hidden rounded-lg border bg-card text-[12px]"
      >
        {/* ── Faixa 1: cabeçalho de uma linha ───────────────────────────── */}
        <header className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5 border-b bg-muted/30 px-3 py-1.5">
          <div className="flex min-w-0 items-center gap-2">
            <span className="truncate text-[12px] font-semibold text-foreground">
              {progresso.definicao?.nome ?? 'Esteira do processo'}
            </span>
            {progresso.definicao?.versao != null && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    className={cn(
                      'rounded-sm border bg-background px-1 text-[10px]',
                      // Versão vigente x versão da instância (v1.3.0 da RPC)
                      progresso.desatualizada ? 'text-warning' : 'text-muted-foreground',
                    )}
                  >
                    v{progresso.definicao.versao}
                  </span>
                </TooltipTrigger>
                {progresso.desatualizada ? (
                  <TooltipContent className="max-w-[260px]">
                    Versão vigente do processo. Este registro ainda está ancorado na v
                    {progresso.definicao.versao_instancia ?? '?'} — clique em "Reavaliar" para
                    migrar para a versão atual.
                  </TooltipContent>
                ) : (
                  <TooltipContent>Versão vigente do processo</TooltipContent>
                )}
              </Tooltip>
            )}
            <span className="hidden h-3 w-px bg-border sm:block" />
            <span className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
              <span className={cn('h-1.5 w-1.5 rounded-full', STATUS_DOT[statusKey] ?? 'bg-muted-foreground')} />
              {statusLabel}
            </span>
            {desdeRelativo && (
              <span className="hidden text-[10px] text-muted-foreground/70 sm:inline">· {desdeRelativo}</span>
            )}
            {resumoAlerta && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button type="button" aria-label="Ver falhas do processo" className="inline-flex">
                    <AlertTriangle className="h-3.5 w-3.5 text-destructive" />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-[320px]">{resumoAlerta}</TooltipContent>
              </Tooltip>
            )}
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="wf-only-mine" className="text-[11px] text-muted-foreground">
                Somente minhas
              </Label>
              <Switch
                id="wf-only-mine"
                className="scale-[0.7]"
                checked={somenteMinhas}
                onCheckedChange={setSomenteMinhas}
              />
            </div>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-6 gap-1.5 px-2 text-[11px]"
                  onClick={reavaliar}
                  disabled={reavaliando}
                >
                  <RefreshCw className={cn('h-3 w-3', reavaliando && 'animate-spin')} />
                  Reavaliar
                </Button>
              </TooltipTrigger>
              <TooltipContent className="max-w-[260px]">
                Reaplica a versão vigente do processo neste registro: entra, sai ou permanece conforme
                as regras publicadas hoje.
              </TooltipContent>
            </Tooltip>
          </div>
        </header>

        {/* ── Faixa 2: timeline vertical detalhada ──────────────────────── */}
        {/* v1.5.0: os mesmos dados da trilha horizontal agora ficam sempre
            visíveis em uma coluna auditável. Nenhuma regra do motor mudou. */}
        <div className={cn('border-t px-3 py-4 sm:px-5', encerrado && 'opacity-90')}>
          <div className="mb-4 flex items-center justify-between gap-3">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Andamento do fluxo
            </span>
            <Badge variant="outline" className="h-5 bg-background px-2 text-[9px] font-medium">
              {etapas.length} {etapas.length === 1 ? 'etapa percorrida' : 'etapas percorridas'}
            </Badge>
          </div>

          <div className="relative space-y-5">
            {(etapas.length > 0 || proximos.length > 0) && (
              <div className="absolute bottom-5 left-[17px] top-5 w-px bg-border" aria-hidden="true" />
            )}

            {etapas.map((etapa, i) => {
              // v1.1.0: a etapa atual pode vir sinalizada pela RPC ('atual') OU
              // pelo casamento com a etapa atual devolvida no topo. Defesa em
              // profundidade contra divergência de versão do processo.
              // Em processo encerrado nenhuma etapa é "atual".
              const isAtual = !encerrado && (etapa.atual === true || etapa.node_id === atualId);
              // Só é "concluída" quando a execução realmente terminou. Execução
              // ainda aberta (sem finalizado_em) nunca recebe o check verde.
              const isConcluida =
                !isAtual && (Boolean(etapa.finalizado_em) || etapa.status === 'concluido');
              // Etapa que ficou aberta em processo cancelado: encerrada junto
              // com o processo (evita o falso positivo de "em andamento").
              const isCancelada =
                !isConcluida && (etapa.status === 'cancelado' || (encerrado && cancelado));
              // v1.7.0: etapa final ('fim') concluída recebe destaque vermelho pastel
              const isFimConcluida = isConcluida && etapa.node_type === 'fim';
              const statusEtapa = isAtual
                ? 'Em andamento'
                : isConcluida
                  ? 'Concluída'
                  : isCancelada
                    ? 'Cancelada'
                    : 'Aguardando';
              // BR-WF-OS-002: ações recusadas desta etapa (motor conclui a etapa mesmo assim)
              const falhasEtapa = acoesFalhas(etapa.detalhes?.acoes_executadas);
              const tarefasDaEtapa = tarefasPorEtapa.get(etapa.node_id) ?? [];
              const decisoesDaEtapa = decisoesPorEtapa.get(etapa.node_id) ?? [];
              const chaveDetalhes = `etapa-${etapa.node_id}`;
              const faseAberta = !!detalhesFases[chaveDetalhes];
              return (
                <div key={`${etapa.node_id}-${etapa.ocorrido_em}`} className="relative flex gap-4">
                  <span
                    className={cn(
                      'relative z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-4 border-background',
                      isAtual
                        ? 'bg-primary/15 text-primary ring-1 ring-primary/20'
                        : isFimConcluida
                          ? 'bg-red-100 text-red-500 ring-1 ring-red-200'
                          : isConcluida
                            ? 'bg-success/15 text-success ring-1 ring-success/20'
                            : isCancelada
                              ? 'bg-destructive/10 text-destructive ring-1 ring-destructive/15'
                              : 'bg-muted text-muted-foreground ring-1 ring-border',
                    )}
                  >
                    {isAtual ? (
                      <span className="h-2.5 w-2.5 rounded-full bg-primary" />
                    ) : isConcluida ? (
                      <Check className="h-4 w-4" />
                    ) : isCancelada ? (
                      <XCircle className="h-4 w-4" />
                    ) : (
                      <Hourglass className="h-4 w-4" />
                    )}
                  </span>

                  <div className="min-w-0 flex-1 rounded-md border border-border px-3 py-3">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className={cn('text-[12px] font-semibold text-foreground', isCancelada && 'line-through')}>
                          {i + 1}. {etapa.label}
                        </p>
                        <p className="mt-0.5 text-[10px] text-muted-foreground">
                          {isAtual ? 'Iniciada' : isConcluida ? 'Concluída' : 'Registrada'} em{' '}
                          {formatarData(isConcluida ? (etapa.finalizado_em ?? etapa.ocorrido_em) : etapa.ocorrido_em)}
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className={cn('flex items-center gap-1.5 text-[10px] font-medium text-foreground', falhasEtapa.length > 0 && 'text-destructive')}>
                          <span className={cn('h-2 w-2 rounded-full', isAtual && 'bg-primary', isConcluida && 'bg-success', isCancelada && 'bg-destructive', falhasEtapa.length > 0 && 'bg-destructive', !isAtual && !isConcluida && !isCancelada && 'bg-muted-foreground')} />
                          {rotuloEtapa(statusEtapa, falhasEtapa.length)}
                        </span>
                        <Button type="button" variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground" aria-expanded={faseAberta} aria-label={faseAberta ? 'Recolher detalhes da fase' : 'Expandir detalhes da fase'} onClick={() => setDetalhesFases((atual) => ({ ...atual, [chaveDetalhes]: !atual[chaveDetalhes] }))}>
                          {faseAberta ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                        </Button>
                      </div>
                    </div>

                    {faseAberta && (
                      <div className="mt-3 border-t pt-3">
                        <DetalhesDaFase nodeType={etapa.node_type} detalhes={etapa.detalhes} />
                      </div>
                    )}

                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-muted-foreground">
                      <span>Tipo: {etapa.node_type || 'Etapa'}</span>
                      {isAtual && desdeRelativo && <span>Em andamento {desdeRelativo}</span>}
                      {isAtual && progresso.etapa_atual?.prazo && (
                        <span>Prazo: {formatarData(progresso.etapa_atual.prazo)}</span>
                      )}
                      {isAtual && (progresso.etapa_atual?.pendencias_abertas ?? 0) > 0 && (
                        <span className="font-medium text-warning">
                          {progresso.etapa_atual?.pendencias_abertas} pendência(s) em aberto
                        </span>
                      )}
                    </div>

                    {/* BR-WF-OS-002: motivo da ação recusada + reprocessar */}
                    <WorkflowStepFailures instanceId={progresso.instance_id} falhas={falhasEtapa} />

                    {carregandoTarefas && isAtual && (
                      <p className="mt-3 border-t pt-3 text-[11px] text-muted-foreground">Carregando pendências…</p>
                    )}
                    {tarefasDaEtapa.length > 0 && (
                      <div className="mt-3 space-y-3 border-t pt-3">{tarefasDaEtapa.map(renderPendencia)}</div>
                    )}
                    {decisoesDaEtapa.length > 0 && (
                      <div className="mt-3 border-t pt-3">
                        <p className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">Histórico da etapa</p>
                        <div className="mt-3">
                          {decisoesDaEtapa.map((decisao, indice) => renderDecisao(decisao, indice, decisoesDaEtapa.length))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {proximos.map((passo, i) => {
              const chaveDetalhes = `proxima-${passo.node_id}-${passo.event_code ?? 'auto'}`;
              const faseAberta = !!detalhesFases[chaveDetalhes];
              return (
              <div key={`prox-${passo.node_id}-${passo.event_code ?? 'auto'}`} className="relative flex gap-4">
                <span className="relative z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-dashed border-border bg-muted text-muted-foreground ring-4 ring-background">
                  <IconePasso passo={passo} className="h-4 w-4" />
                </span>
                <div className="min-w-0 flex-1 rounded-md border border-dashed bg-muted/20 px-3 py-3">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-[12px] font-semibold text-muted-foreground">
                        {etapas.length + i + 1}. {passo.label}
                      </p>
                      <p className="mt-1 text-[10px] text-muted-foreground">
                        {passo.aresta_label ? `${passo.aresta_label} · ` : ''}
                        {descreverGatilho(passo)}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="flex items-center gap-1.5 text-[10px] font-medium text-foreground"><span className="h-2 w-2 rounded-full bg-muted-foreground" />Próxima etapa</span>
                      <Button type="button" variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground" aria-expanded={faseAberta} aria-label={faseAberta ? 'Recolher detalhes da fase' : 'Expandir detalhes da fase'} onClick={() => setDetalhesFases((atual) => ({ ...atual, [chaveDetalhes]: !atual[chaveDetalhes] }))}>
                        {faseAberta ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                      </Button>
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {passo.tem_condicao && <Badge variant="secondary" className="h-5 text-[9px]">Com condição</Badge>}
                    {passo.is_default && <Badge variant="secondary" className="h-5 text-[9px]">Caminho padrão</Badge>}
                    {passo.enforcement === 'block' && (
                      <Badge variant="destructive" className="h-5 text-[9px]">Bloqueia o avanço</Badge>
                    )}
                    {passo.enforcement === 'warn' && (
                      <Badge variant="outline" className="h-5 border-warning/30 bg-warning/10 text-[9px] text-warning">
                        Exibe aviso
                      </Badge>
                    )}
                  </div>
                  {faseAberta && <div className="mt-3 border-t pt-3"><DetalhesDaFase nodeType={passo.node_type} detalhes={passo.detalhes} /></div>}
                </div>
              </div>
              );
            })}

            {/* Processo encerrado: deixa claro que não há mais nada a acontecer */}
            {encerrado && (
              <div className="relative flex gap-4">
                <span className="relative z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground ring-4 ring-background">
                  <CheckCircle2 className="h-4 w-4" />
                </span>
                <div className="flex min-h-9 flex-1 items-center rounded-md border bg-background px-3 text-[11px] text-muted-foreground">
                  Processo encerrado — nenhum passo pendente.
                </div>
              </div>
            )}

            {!encerrado && etapas.length === 0 && proximos.length === 0 && (
              <span className="text-[11px] text-muted-foreground">
                Não há próximos passos configurados a partir desta etapa.
              </span>
            )}

            {(tarefasSemEtapa.length > 0 || decisoesSemEtapa.length > 0) && (
              <div className="relative flex gap-4">
                <span className="relative z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground ring-4 ring-background">
                  <AlertTriangle className="h-4 w-4" />
                </span>
                <div className="min-w-0 flex-1 rounded-md border border-dashed border-border px-3 py-3">
                  <p className="text-[11px] font-semibold text-foreground">Registros de versão anterior</p>
                  <p className="mt-0.5 text-[10px] text-muted-foreground">Estes registros não possuem vínculo com uma fase da versão atual.</p>
                  {tarefasSemEtapa.length > 0 && <div className="mt-3 space-y-3 border-t pt-3">{tarefasSemEtapa.map(renderPendencia)}</div>}
                  {decisoesSemEtapa.length > 0 && (
                    <div className="mt-3 border-t pt-3">
                      {decisoesSemEtapa.map((decisao, indice) => renderDecisao(decisao, indice, decisoesSemEtapa.length))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </TooltipProvider>
  );
}
